import { api, getCurrentAccessToken, readAPIResponse } from './api';
import { getAPIBaseURL } from './api-url';
import type { PublicCvSharePayload } from './cv-public-document';

export type CvTargetKey = 'master' | string;

export class CvCollaborationApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'CvCollaborationApiError';
  }
}

export interface CvLanguageVariant {
  revision: number;
  sourceState?: "current" | "changed" | "unknown";
  needsReview?: boolean;
  sourceRevisionId?: string | null;
  id: string;
  cvId: string | null;
  ownerUserId: string;
  targetKey: string;
  locale: string;
  direction: 'ltr' | 'rtl' | string;
  title: string | null;
  summary: string | null;
  headline: string | null;
  content: Record<string, unknown>;
  status: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Only on the generate response. Units the server could not translate with
   * every fact intact keep the source text and are listed in `needsReview`.
   */
  translation?: CvLanguageVariantTranslationReport;
}

export interface CvLanguageVariantTranslationReport {
  version: 2;
  sourceHash: string;
  unitCount: number;
  translatedCount: number;
  needsReview: { path: string; reason: string }[];
}

export interface CvAccessGrant {
  id: string;
  cvId: string | null;
  ownerUserId: string;
  targetKey: string;
  granteeUserId: string | null;
  granteeCompanyId?: string | null;
  granteeEmail: string | null;
  role: string;
  permissions: string[];
  status: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CvReceivedAccessGrant extends CvAccessGrant {
  delegatedTarget: string;
  targetTitle: string;
  targetKind: 'master' | 'tailored' | string;
  owner: {
    id: string;
    email: string;
    name: string;
    username: string;
    image: string | null;
  };
}

export interface CvShareLink {
  editionKey?: string;
  releaseMode?: "legacy_live" | "follow_approved" | "exact" | "public";
  releaseId?: string | null;
  releaseKey?: string | null;
  id: string;
  targetKey: string;
  variantId: string | null;
  label: string | null;
  permissions: string[];
  visibility: Record<string, unknown>;
  allowDownload: boolean;
  expiresAt: string | null;
  accessCount: number;
  tokenPreview: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CvActivityLog {
  id: string;
  action: string;
  targetKind: string;
  targetId: string | null;
  actorEmail: string | null;
  actorKind: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface CreateShareLinkResult {
  shareLink: CvShareLink;
  token: string;
  shareUrl?: string | null;
  legacyShareUrl?: string | null;
}

export interface CreateAccessGrantResult {
  grant: CvAccessGrant;
  inviteToken?: string | null;
  inviteUrl?: string | null;
}

export interface AcceptedAccessGrant extends CvAccessGrant {
  delegatedTarget?: string;
}

function throwCvCollaborationError(error: {
  code: string;
  message: string;
}): never {
  throw new CvCollaborationApiError(error.message, error.code);
}

export async function fetchCvLanguageVariants(
  target: CvTargetKey,
): Promise<CvLanguageVariant[]> {
  const res = await api.get<CvLanguageVariant[]>(`/api/cv/${target}/variants`);
  if (res.error) throwCvCollaborationError(res.error);
  return res.data ?? [];
}

export async function generateCvLanguageVariant(
  target: CvTargetKey,
  input: { locale: string; direction?: 'ltr' | 'rtl'; status?: 'draft' | 'published' },
): Promise<CvLanguageVariant> {
  const res = await api.post<CvLanguageVariant>(
    `/api/cv/${target}/variants/generate`,
    input,
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

export async function fetchCvAccessGrants(
  target: CvTargetKey,
): Promise<CvAccessGrant[]> {
  const res = await api.get<CvAccessGrant[]>(`/api/cv/${target}/access-grants`);
  if (res.error) throwCvCollaborationError(res.error);
  if (!Array.isArray(res.data)) throw new Error("Could not load CV access");
  return res.data;
}

export async function fetchReceivedCvAccessGrants(): Promise<CvReceivedAccessGrant[]> {
  const res = await api.get<CvReceivedAccessGrant[]>('/api/cv/access-grants/received');
  if (res.error) throwCvCollaborationError(res.error);
  return res.data ?? [];
}

export async function createCvAccessGrant(
  target: CvTargetKey,
  input: { granteeEmail: string; role: 'viewer' | 'editor' | 'admin' },
): Promise<CreateAccessGrantResult> {
  const res = await api.post<CreateAccessGrantResult>(
    `/api/cv/${target}/access-grants`,
    input,
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

export async function updateCvAccessGrant(
  target: CvTargetKey,
  grantId: string,
  input: {
    role?: 'viewer' | 'editor' | 'admin';
    permissions?: string[];
    status?: 'pending' | 'active' | 'revoked';
    expiresAt?: string | null;
  },
): Promise<CvAccessGrant> {
  const res = await api.patch<CvAccessGrant>(
    `/api/cv/${target}/access-grants/${grantId}`,
    input,
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

export async function revokeCvAccessGrant(
  target: CvTargetKey,
  grantId: string,
): Promise<CvAccessGrant> {
  const res = await api.delete<CvAccessGrant>(
    `/api/cv/${target}/access-grants/${grantId}`,
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

export async function fetchCvShareLinks(
  target: CvTargetKey,
): Promise<CvShareLink[]> {
  const res = await api.get<CvShareLink[]>(`/api/cv/${target}/share-links`);
  if (res.error) throwCvCollaborationError(res.error);
  if (!Array.isArray(res.data)) throw new Error("Could not load CV access");
  return res.data;
}

export async function createCvShareLink(
  target: CvTargetKey,
  input: {
    variantId?: string | null;
    releaseMode?: "follow_approved" | "exact";
    releaseId?: string;
    label?: string | null;
    permissions?: string[];
    visibility?: Record<string, unknown>;
    allowDownload?: boolean;
    revokeExisting?: boolean;
  },
): Promise<CreateShareLinkResult> {
  const res = await api.post<CreateShareLinkResult>(
    `/api/cv/${target}/share-links`,
    input,
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

/**
 * The live standing private link, or null when there is none. Read-only: it
 * never issues one, so the Share sheet can show a link made earlier (or on
 * another device) without minting a token just by opening.
 */
export async function fetchCvPrivateLink(
  target: CvTargetKey,
): Promise<CreateShareLinkResult | null> {
  const res = await api.get<CreateShareLinkResult | null>(
    `/api/cv/${target}/private-link`,
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data ?? null;
}

/**
 * The standing private link for this CV: the same URL on every device, because
 * the API keeps this one link's token rather than only its hash. `rotate`
 * retires it and issues a new one (the Regenerate action).
 */
export async function ensureCvPrivateLink(
  target: CvTargetKey,
  input: {
    permissions?: string[];
    visibility?: Record<string, unknown>;
    allowDownload?: boolean;
    rotate?: boolean;
  } = {},
): Promise<CreateShareLinkResult> {
  const res = await api.post<CreateShareLinkResult>(
    `/api/cv/${target}/private-link`,
    input,
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

export async function ensureCvPublicLink(
  target: CvTargetKey,
): Promise<CreateShareLinkResult> {
  const res = await api.post<CreateShareLinkResult>(
    `/api/cv/${target}/public-link`,
    {},
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

export async function updateCvShareLink(
  target: CvTargetKey,
  shareLinkId: string,
  input: {
    visibility?: Record<string, unknown>;
    allowDownload?: boolean;
    revoked?: boolean;
  },
): Promise<CvShareLink> {
  const res = await api.patch<CvShareLink>(
    `/api/cv/${target}/share-links/${shareLinkId}`,
    input,
  );
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

export async function fetchCvActivity(
  target: CvTargetKey,
): Promise<CvActivityLog[]> {
  const res = await api.get<CvActivityLog[]>(`/api/cv/${target}/activity`);
  if (res.error) throwCvCollaborationError(res.error);
  return res.data ?? [];
}

export async function acceptCvAccessGrant(
  token: string,
): Promise<AcceptedAccessGrant> {
  const res = await api.post<AcceptedAccessGrant>('/api/cv/access-grants/accept', {
    token,
  });
  if (res.error) throwCvCollaborationError(res.error);
  return res.data!;
}

export async function fetchPublicCvShare(
  token: string,
  options: { handle?: string | null } = {},
): Promise<PublicCvSharePayload> {
  const encodedToken = encodeURIComponent(token);
  const query = options.handle
    ? `?handle=${encodeURIComponent(options.handle)}`
    : '';
  // A public call, but a signed-in owner opening their own link must not count
  // as a view, so the in-memory access token rides along when there is one.
  // Never refresh or redirect here: no token simply means an anonymous view.
  const accessToken = getCurrentAccessToken();
  const response = await fetch(
    `${getAPIBaseURL()}/api/cv/share-links/${encodedToken}${query}`,
    {
      credentials: 'include',
      ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
    },
  );
  const envelope = await readAPIResponse<PublicCvSharePayload>(response);
  if (envelope.error) throw new Error(envelope.error.message);
  if (!envelope.data) throw new Error('Share link not found');
  return withPublicCvShareDownloadFallback(envelope.data, encodedToken);
}

export function withPublicCvShareDownloadFallback(
  payload: PublicCvSharePayload,
  encodedToken: string,
): PublicCvSharePayload {
  if (
    !payload.shareLink.allowDownload ||
    (payload.shareLink.downloadUrls &&
      Object.keys(payload.shareLink.downloadUrls).length > 0)
  ) {
    return payload;
  }

  return {
    ...payload,
    shareLink: {
      ...payload.shareLink,
      downloadUrls: {
        pdf: `${getAPIBaseURL()}/api/cv/share-links/${encodedToken}/export/pdf`,
      },
    },
  };
}
